// src/pages/insights/Notifications.jsx
//
// Inbox for the selected organization, driven by the real backend:
// GET /api/notifications/?org_id= returns Notification rows created on real
// events (contribution created, expense submitted / approved / rejected).
//
// Senior Accounting Clerks (hide_notifications permission) additionally see
// a per-notification eye toggle. Toggling calls PATCH /<id>/hide/ which
// flips is_hidden on the backend. Hidden notifications are filtered out of
// every other user's query by the backend — Sr. Clerks still see them with
// a dimmed "Hidden" badge so they know what is suppressed.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiCall } from '../../api';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

// "2026-08-12T09:30:00Z" -> "2 days ago" style relative time.
const relativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const seconds = Math.round((Date.now() - then.getTime()) / 1000);
  const intervals = [
    { seconds: 31536000, label: 'year' },
    { seconds: 2592000, label: 'month' },
    { seconds: 604800, label: 'week' },
    { seconds: 86400, label: 'day' },
    { seconds: 3600, label: 'hour' },
    { seconds: 60, label: 'minute' },
  ];
  for (const interval of intervals) {
    const count = Math.floor(seconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${count === 1 ? '' : 's'} ago`;
    }
  }
  return 'just now';
};

// Eye-on SVG icon
const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Eye-off SVG icon
const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const Notifications = ({ ngo, userRole, onBack, onHome, onEditProfile }) => {
  const { user, getAccessTokenSilently, logout } = useAuth0();
  const { t } = useTranslation('notifications');
  const { t: tc } = useTranslation('common');

  const isSrClerk = userRole === 'senior_accounting_clerk';

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Track which notification IDs are currently having their hide state toggled
  const [togglingIds, setTogglingIds] = useState(new Set());

  // ---- Profile dropdown state (§3) ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  useEffect(() => {
    if (!ngo?.id) return;

    let cancelled = false;

    const fetchNotifications = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await apiCall(
          `/api/notifications/?org_id=${ngo.id}`,
          {},
          getAccessTokenSilently
        );
        if (!cancelled) {
          setNotifications(Array.isArray(data) ? data.map(n => ({
            id: n.id,
            title: n.type_label || 'Notification',
            message: n.message,
            time: relativeTime(n.created_at),
            read: n.is_read,
            hidden: n.is_hidden,
          })) : []);
        }
      } catch (err) {
        if (!cancelled) setError(tc('error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchNotifications();

    return () => {
      cancelled = true;
    };
  }, [ngo, getAccessTokenSilently, tc]);

  const markAllRead = async () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
    try {
      await apiCall(
        `/api/notifications/mark-all-read/?org_id=${ngo.id}`,
        { method: 'PATCH' },
        getAccessTokenSilently
      );
    } catch (err) {
      // Non-fatal: keep the optimistic UI state.
    }
  };

  const markRead = async (id) => {
    setNotifications(notifications.map(n => n.id === id ? { ...n, read: true } : n));
    try {
      await apiCall(
        `/api/notifications/${id}/read/`,
        { method: 'PATCH' },
        getAccessTokenSilently
      );
    } catch (err) {
      // Non-fatal: keep the optimistic UI state.
    }
  };

  // Toggle the hidden state for a notification (Sr. Clerk only).
  // Optimistically flips the local state immediately, then syncs to backend.
  // On failure, rolls back to the original state.
  const toggleHide = useCallback(async (e, id) => {
    // Prevent the card's markRead click handler from firing.
    e.stopPropagation();

    // Prevent double-taps while an in-flight request is pending.
    if (togglingIds.has(id)) return;

    const original = notifications.find(n => n.id === id);
    if (!original) return;

    // Optimistic update
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, hidden: !n.hidden } : n)
    );
    setTogglingIds(prev => new Set([...prev, id]));

    try {
      const updated = await apiCall(
        `/api/notifications/${id}/hide/?org_id=${ngo.id}`,
        { method: 'PATCH' },
        getAccessTokenSilently
      );
      // Sync authoritative state from server
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, hidden: updated.is_hidden } : n)
      );
    } catch (err) {
      // Roll back on error
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, hidden: original.hidden } : n)
      );
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [ngo, getAccessTokenSilently, notifications, togglingIds]);

  const unreadCount = notifications.filter(n => !n.read && !n.hidden).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        <button onClick={onHome} aria-label="Home" title="Home" className="text-blue-600 hover:text-blue-700 flex-shrink-0">
          <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>

        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <span className="text-sm font-medium text-gray-900 flex-1 flex items-center gap-2">
          {t('title')}
          {unreadCount > 0 && (
            <span className="bg-blue-600 text-white rounded-full text-xs font-semibold px-2 py-0.5">
              {unreadCount}
            </span>
          )}
        </span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* ---- USER AVATAR + PROFILE DROPDOWN (§3) ---- */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {/* Sr. Clerk hint banner */}
        {isSrClerk && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" className="flex-shrink-0">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
            <p className="text-xs text-amber-700 font-medium">
              Use the eye icon to hide notifications from other users.
            </p>
          </div>
        )}

        {unreadCount > 0 && (
          <div className="flex justify-end mb-3">
            <button
              onClick={markAllRead}
              className="border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg py-2.5 px-3 text-xs font-medium transition"
            >
              {t('markAllRead')}
            </button>
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          {loading && (
            <p className="text-center text-xs text-gray-400">{t('loading')}</p>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
              <p className="text-xs text-red-600 text-center">{error}</p>
            </div>
          )}

          {!loading && !error && notifications.length === 0 && (
            <p className="text-center text-xs text-gray-400">{t('noData')}</p>
          )}

          {!loading && !error && notifications.map(n => (
            <div
              key={n.id}
              onClick={() => !n.hidden && markRead(n.id)}
              className={`flex items-start gap-2.5 rounded-xl px-3.5 py-3 border transition-opacity ${
                n.hidden
                  ? 'bg-gray-50 border-gray-200 opacity-60 cursor-default'
                  : n.read
                    ? 'bg-white border-gray-200 cursor-pointer'
                    : 'bg-blue-50 border-blue-200 cursor-pointer'
              }`}
            >
              {/* Unread dot */}
              <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                n.hidden ? 'bg-gray-300' : n.read ? 'bg-gray-300' : 'bg-blue-600'
              }`} />

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    <p className={`text-xs font-semibold truncate ${n.hidden ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                      {n.title}
                    </p>
                    {/* Hidden badge — visible only to Sr. Clerk */}
                    {isSrClerk && n.hidden && (
                      <span className="flex-shrink-0 text-[10px] font-semibold bg-gray-200 text-gray-500 rounded px-1.5 py-0.5">
                        Hidden
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 flex-shrink-0">{n.time}</p>
                </div>
                <p className={`text-xs ${n.hidden ? 'text-gray-400' : 'text-gray-500'}`}>{n.message}</p>
              </div>

              {/* ---- Hide toggle (Sr. Clerk only) ---- */}
              {isSrClerk && (
                <button
                  onClick={(e) => toggleHide(e, n.id)}
                  disabled={togglingIds.has(n.id)}
                  title={n.hidden ? 'Show to all users' : 'Hide from other users'}
                  aria-label={n.hidden ? 'Show notification' : 'Hide notification'}
                  className={`flex-shrink-0 mt-0.5 p-1 rounded-md transition-colors disabled:opacity-40 ${
                    n.hidden
                      ? 'text-amber-500 hover:bg-amber-50'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                  }`}
                >
                  {n.hidden ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Notifications;